import React, { useState, useRef, useCallback, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import Avatar from "../shared/Avatar";
import styles from "./Topbar.module.css";
import { useLanguage } from "../../context/LanguageContext";
import { buildTopbarMenuModel } from "./topbarMenuConfig";

/* ── Lightweight SVG icons (no heavy library needed) ────────────────── */
const IconHome = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
  </svg>
);
const IconPeople = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);
const IconBell = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  </svg>
);
const IconEnvelope = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);
const IconPersonAdd = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);
const IconHamburger = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
  </svg>
);
const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);
const IconSettings = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);
const IconGroup = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91zM4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58C.48 14.9 0 15.62 0 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29zM20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 3.43c0-.81-.48-1.53-1.22-1.85A6.95 6.95 0 0 0 20 14c-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24v-1.57zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
  </svg>
);
const IconAdmin = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
  </svg>
);
const IconMembers = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 5.9c1.16 0 2.1.94 2.1 2.1s-.94 2.1-2.1 2.1S9.9 9.16 9.9 8s.94-2.1 2.1-2.1m0 9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 9c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z" />
  </svg>
);
const IconForum = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M4 4h16c1.1 0 2 .9 2 2v9c0 1.1-.9 2-2 2H7l-5 4V6c0-1.1.9-2 2-2zm2 5v2h12V9H6zm0 4v2h8v-2H6z" />
  </svg>
);

const ICON_MAP = {
  home: IconHome,
  people: IconPeople,
  bell: IconBell,
  envelope: IconEnvelope,
  personAdd: IconPersonAdd,
  calendar: IconCalendar,
  members: IconMembers,
  group: IconGroup,
  forum: IconForum,
};

/**
 * Top navigation bar — black bar, 55px, fixed to top.
 *
 * @param {object}  props
 * @param {object}  props.user     - User object { first_name, last_name, username, photo, accountRole }
 * @param {function} props.onLogout - Logout callback
 */
export default function Topbar({ user, onLogout }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { t } = useLanguage();
  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [expandedMore, setExpandedMore] = useState(null);
  const [navDropdownOpen, setNavDropdownOpen] = useState(null);

  // Read enableInvites from the cached public-config (fetched by useBranding)
  const publicConfig = queryClient.getQueryData(["public-config"]);
  const enableInvites = publicConfig?.general?.enableInvites !== false; // default true
  const forumEnabled = publicConfig?.features?.forum !== false;
  const groupCategories = useMemo(
    () => publicConfig?.groupCategories || [],
    [publicConfig]
  );

  const { primaryItems, moreItems } = useMemo(
    () =>
      buildTopbarMenuModel(t, {
        enableInvites,
        forumEnabled,
        groupCategories,
      }),
    [enableInvites, forumEnabled, groupCategories, t]
  );

  const menuRef = useRef(null);
  const moreRef = useRef(null);
  const navDropdownRef = useRef(null);
  useOnClickOutside(menuRef, menuOpen, () => setMenuOpen(false));
  useOnClickOutside(moreRef, moreOpen, () => { setMoreOpen(false); setExpandedMore(null); });
  useOnClickOutside(navDropdownRef, !!navDropdownOpen, () => setNavDropdownOpen(null));

  const closeAll = useCallback(() => {
    setNavOpen(false);
    setMenuOpen(false);
    setMoreOpen(false);
    setExpandedMore(null);
    setNavDropdownOpen(null);
  }, []);

  const displayName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || ""
    : "";

  const profileHref = user?.username ? `/u/${user.username}` : "/profile";
  const friendsHref = user?.username ? `/u/${user.username}/friends` : "/friends";
  const isGroupsRoute =
    location.pathname.startsWith("/groups") || location.pathname.startsWith("/group/");

  const handleLogout = () => {
    closeAll();
    if (onLogout) onLogout();
  };

  return (
    <header className={styles.topbar} role="banner">
      <nav className={styles.inner} aria-label="Main navigation">
        {/* ── Mobile hamburger ────────────────────────────────────────── */}
        <button
          className={styles.hamburger}
          onClick={() => setNavOpen((v) => !v)}
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
        >
          <IconHamburger />
        </button>

        {/* ── Left nav links ──────────────────────────────────────────── */}
        <ul className={`${styles.navList} ${navOpen ? styles.navOpen : ""}`.trim()}>
          {primaryItems.map((item) => {
            const Icon = ICON_MAP[item.icon];
            const href = item.dynamicTo ? friendsHref : item.link;

            if (item.submenu) {
              const isOpen = navDropdownOpen === item.key;
              const isActive = isGroupsRoute;
              const navItemClass = `${styles.navItem} ${
                item.promoteRight ? styles.navItemPushRight : ""
              }`.trim();
              const dropdownClass = `${styles.navDropdown} ${
                item.alignRight ? styles.navDropdownRight : ""
              }`.trim();

              return (
                <li
                  key={item.key}
                  className={navItemClass}
                  ref={isOpen ? navDropdownRef : null}
                >
                  <button
                    className={`${styles.navLink} ${
                      isActive || isOpen ? styles.navLinkActive : ""
                    }`.trim()}
                    onClick={() =>
                      setNavDropdownOpen((current) =>
                        current === item.key ? null : item.key
                      )
                    }
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    type="button"
                  >
                    <Icon />
                    <span className={styles.navLabel}>{item.label}</span>
                    <IconChevronDown />
                  </button>
                  {isOpen && (
                    <div className={dropdownClass}>
                      {item.submenu.map((subItem) => {
                        if (subItem.type === "divider") {
                          return (
                            <div
                              key={subItem.key}
                              className={styles.navDropdownDivider}
                            />
                          );
                        }

                        if (subItem.type === "section") {
                          return (
                            <div
                              key={subItem.key}
                              className={styles.navDropdownSection}
                            >
                              {subItem.label}
                            </div>
                          );
                        }

                        return (
                          <Link
                            key={subItem.key}
                            to={subItem.link}
                            className={`${styles.navDropdownItem} ${
                              subItem.accent ? styles.navDropdownItemAccent : ""
                            }`.trim()}
                            onClick={closeAll}
                          >
                            {subItem.icon && (
                              <i
                                className={subItem.icon}
                                style={{ marginRight: 6, fontSize: 11 }}
                              ></i>
                            )}
                            {subItem.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            }

            return (
              <li
                key={item.key}
                className={`${styles.navItem} ${
                  item.promoteRight ? styles.navItemPushRight : ""
                }`.trim()}
              >
                <NavLink
                  to={href}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`.trim()
                  }
                  onClick={closeAll}
                >
                  <Icon />
                  <span className={styles.navLabel}>{item.label}</span>
                </NavLink>
              </li>
            );
          })}

          {/* More — toggles inline NIHSS hamburger dropdown */}
          <li className={styles.navItem} style={{ position: "relative" }}>
            <button
              className={styles.navLink}
              style={{ background: "none", border: "none", cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); setMoreOpen((v) => !v); }}
              aria-haspopup="true"
              aria-expanded={moreOpen}
            >
              <IconHamburger />
              <span className={styles.navLabel}>{t("topbar_more", "More")}</span>
            </button>
            {moreOpen && (
              <div
                ref={moreRef}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "#fff",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,.18)",
                  width: 280,
                  maxHeight: "70vh",
                  overflowY: "auto",
                  zIndex: 1100,
                  padding: "10px 0",
                }}
              >
                {moreItems.map((item, idx) => (
                  <div key={idx}>
                    {item.submenu ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 14px",
                            cursor: "pointer",
                            fontWeight: 500,
                            fontSize: 14,
                            color: "#333",
                          }}
                          onClick={() => setExpandedMore(expandedMore === idx ? null : idx)}
                        >
                          <span>
                            <i className={item.icon} style={{ width: 20, marginRight: 8, color: "#555" }}></i>
                            {item.label}
                          </span>
                          <i className={`fa fa-chevron-${expandedMore === idx ? "up" : "down"}`} style={{ fontSize: 10, color: "#888" }}></i>
                        </div>
                        {expandedMore === idx && item.submenu.map((sub, si) => (
                          <Link
                            key={si}
                            to={sub.link}
                            style={{
                              display: "block",
                              padding: "6px 14px 6px 42px",
                              fontSize: 13,
                              color: sub.icon ? "#28a745" : "#333",
                              textDecoration: "none",
                              fontWeight: sub.icon ? 600 : 400,
                            }}
                            onClick={() => { setMoreOpen(false); closeAll(); }}
                          >
                            {sub.icon && <i className={sub.icon} style={{ marginRight: 6, fontSize: 11 }}></i>}
                            {sub.label}
                          </Link>
                        ))}
                      </>
                    ) : (
                      <Link
                        to={item.link}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px 14px",
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#333",
                          textDecoration: "none",
                        }}
                        onClick={() => { setMoreOpen(false); closeAll(); }}
                      >
                        <i className={item.icon} style={{ width: 20, marginRight: 8, color: "#555" }}></i>
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </li>
        </ul>

        {/* ── Right side: user avatar + dropdown ──────────────────────── */}
        <div className={styles.rightSection} ref={menuRef}>
          <button
            className={styles.userButton}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <Avatar src={user?.photo} alt={displayName} size="sm" />
            <span className={styles.userName}>{displayName}</span>
            <IconChevronDown />
          </button>

          {/* Dropdown */}
          {menuOpen && (
            <div className={styles.dropdown} role="menu">
              {user?.accountRole === "admin" && (
                <Link
                  to="/administrator"
                  className={styles.dropdownItem}
                  role="menuitem"
                  onClick={closeAll}
                >
                  <IconAdmin />
                  <span>Administrator</span>
                </Link>
              )}
              <Link
                to={`${profileHref}/edit?section=basic`}
                className={styles.dropdownItem}
                role="menuitem"
                onClick={closeAll}
              >
                <IconSettings />
                <span>Account Settings</span>
              </Link>
              <Link
                to="/groups/all"
                className={styles.dropdownItem}
                role="menuitem"
                onClick={closeAll}
              >
                <IconGroup />
                <span>Groups List</span>
              </Link>
              <div className={styles.dropdownDivider} />
              <button
                className={styles.dropdownItem}
                role="menuitem"
                onClick={handleLogout}
              >
                <IconLogout />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
